/**
 * Longevity 360 node L5: CPT to LOINC map for the lab-desk "Enter results" form.
 * Dev orders carry only CPT codes (TestSystem 'CPT'). A test whose code is not
 * here is shown read-only and left out of the saved report.
 */
export interface LabLoincEntry {
  loinc: string;
  unit: string;
  display: string;
  /** Plausible range, inclusive. A value outside it is rejected. */
  min: number;
  max: number;
}

export const CPT_TO_LOINC: Readonly<Record<string, LabLoincEntry>> = {
  '83036': { loinc: '4548-4', unit: '%', display: 'Hemoglobin A1c', min: 3, max: 20 },
  '82947': { loinc: '1558-6', unit: 'mg/dL', display: 'Glucose, fasting', min: 20, max: 800 },
  '82565': { loinc: '2160-0', unit: 'mg/dL', display: 'Creatinine', min: 0.1, max: 20 },
  '82465': { loinc: '2093-3', unit: 'mg/dL', display: 'Cholesterol, total', min: 50, max: 600 },
  '83718': { loinc: '2085-9', unit: 'mg/dL', display: 'HDL cholesterol', min: 5, max: 200 },
  '84478': { loinc: '2571-8', unit: 'mg/dL', display: 'Triglycerides', min: 10, max: 3000 },
  '82172': { loinc: '1884-6', unit: 'mg/dL', display: 'Apolipoprotein B', min: 10, max: 400 },
  '83721': { loinc: '2089-1', unit: 'mg/dL', display: 'LDL cholesterol (direct)', min: 10, max: 400 },
  '83695': { loinc: '43583-4', unit: 'nmol/L', display: 'Lipoprotein(a)', min: 0.1, max: 1000 },
};

/** CPT codes of the lipid trio, and of the direct LDL test. */
export const LIPID_TRIO_CODES = ['82465', '83718', '84478'] as const;
export const DIRECT_LDL_CODE = '83721';

/** LDL the lab calculates from the lipid trio. It has no test row and is not billable. */
export const CALCULATED_LDL: LabLoincEntry = {
  loinc: '13457-7',
  unit: 'mg/dL',
  display: 'LDL cholesterol (calculated)',
  min: 10,
  max: 400,
};

/** True when the shown tests have the lipid trio and no direct LDL, so LDL is calculated. */
export function needsCalculatedLdl(codes: ReadonlyArray<string | null | undefined>): boolean {
  const set = new Set(codes);
  return LIPID_TRIO_CODES.every((c) => set.has(c)) && !set.has(DIRECT_LDL_CODE);
}

export function loincForTestCode(code: string | null | undefined): LabLoincEntry | null {
  return code ? (CPT_TO_LOINC[code] ?? null) : null;
}

/** Null when the value is a plausible result for the test, else the reason. */
export function rangeError(entry: LabLoincEntry, value: number): string | null {
  if (value <= 0 || value < entry.min || value > entry.max) {
    return `Enter a value from ${entry.min} to ${entry.max} ${entry.unit}`;
  }
  return null;
}
